import {getTestProps} from '@benchling/js-test-utils';
import {noop} from '@benchling/js-utils';
import {ActionIcon, createStyles, Menu, UnstyledButton} from '@mantine/core';
import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';

import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {FileViewerContent} from './analysis-workspace/FileViewerContent';
import type {ViewerTab} from './useTabbedViewer';

const PANEL_ID = 'viewer-tabpanel';

export interface TabbedViewerPaneProps {
  tabs: Array<ViewerTab>;
  activeTab: ViewerTab | null;
  onTabChange: (tabId: string) => void;
  onTabClose: (tabId: string) => void;
}

interface PillTabProps {
  tab: ViewerTab;
  index: number;
  isMeasurementOnly?: boolean;
  isActive: boolean;
  onSelect: () => void;
  onClose: () => void;
}

function PillTab({
  tab,
  index,
  isMeasurementOnly = false,
  isActive,
  onSelect,
  onClose,
}: PillTabProps): JSX.Element {
  const {classes, cx} = useStyles();
  const isArtifact = tab.kind === 'artifact';

  return (
    <UnstyledButton
      {...(!isMeasurementOnly &&
        getTestProps({component: 'TabbedViewerPane', element: 'tab', index: String(index)}))}
      id={`tab-${tab.id}`}
      className={cx(classes.pill, isActive && classes.pillActive, isArtifact && classes.pillDoc)}
      role="tab"
      aria-selected={isActive}
      aria-controls={PANEL_ID}
      onClick={onSelect}
    >
      <span className={cx(classes.pillIcon, isActive && classes.pillIconActive)}>
        <BenchlingIcon icon={isArtifact ? 'document' : 'paperclip'} />
      </span>
      <span className={classes.pillLabel}>{tab.label}</span>
      {!isArtifact && (
        <ActionIcon
          {...(!isMeasurementOnly &&
            getTestProps({component: 'TabbedViewerPane', element: 'tabClose', index: String(index)}))}
          component="span"
          role="button"
          size="xs"
          aria-label={`Close ${tab.label}`}
          className={cx(classes.closeButton, isActive && classes.closeButtonActive)}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <BenchlingIcon icon="close" />
        </ActionIcon>
      )}
    </UnstyledButton>
  );
}

/**
 * Determines the index at which tabs should be moved into an overflow menu.
 *
 * Uses a hidden measurement bar (referenced by `measureRef`) that renders all tabs in a
 * single flex row. A ResizeObserver tracks container width changes, and a layout effect
 * walks the children to find the first tab whose right edge exceeds the available space.
 * Space for the overflow button is only reserved when tabs genuinely don't all fit.
 *
 * Returns `null` when all tabs fit, or the index of the first tab that should overflow.
 * Always keeps at least one tab visible (returns minimum index of 1).
 *
 * The measurement bar must have `position: relative` so that child `offsetLeft` values
 * are relative to the bar itself, and its last child must be a placeholder element
 * sized to match the overflow button.
 */
function useOverflowIndex(
  measureRef: React.RefObject<HTMLDivElement | null>,
  tabCount: number
): number | null {
  const [overflowIndex, setOverflowIndex] = useState<number | null>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  useEffect(() => {
    const el = measureRef.current;
    if (el == null) {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [measureRef]);

  useLayoutEffect(() => {
    const bar = measureRef.current;
    if (bar == null) {
      return;
    }
    const overflowButton = bar.lastElementChild as HTMLElement;
    const children = bar.children;
    const count = children.length - 1; // exclude overflow button
    const fullWidth = bar.clientWidth;

    // Check if all tabs fit without needing an overflow button
    const lastTab = children[count - 1] as HTMLElement | undefined;
    if (lastTab != null && lastTab.offsetLeft + lastTab.offsetWidth <= fullWidth) {
      setOverflowIndex(null);
      return;
    }

    // They don't all fit — find the cutoff point reserving space for the overflow button
    const limit = fullWidth - overflowButton.offsetWidth;
    for (let i = 0; i < count; i++) {
      const child = children[i] as HTMLElement;
      if (child.offsetLeft + child.offsetWidth > limit) {
        setOverflowIndex(Math.max(1, i));
        return;
      }
    }
    setOverflowIndex(null);
  }, [measureRef, tabCount, containerWidth]);

  return overflowIndex;
}

export function TabbedViewerPane({
  tabs,
  activeTab,
  onTabChange,
  onTabClose,
}: TabbedViewerPaneProps): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useStyles();
  const measureRef = useRef<HTMLDivElement>(null);

  const activeTabId = activeTab?.id ?? null;

  const effectiveActiveTabId = useMemo(() => {
    if (activeTabId != null && tabs.some((t) => t.id === activeTabId)) {
      return activeTabId;
    }
    return tabs.length > 0 ? tabs[0].id : null;
  }, [activeTabId, tabs]);

  const overflowIndex = useOverflowIndex(measureRef, tabs.length);
  const hasOverflow = overflowIndex != null;

  let visibleTabs = hasOverflow ? tabs.slice(0, overflowIndex) : tabs;
  let overflowTabs = hasOverflow ? tabs.slice(overflowIndex) : [];

  // If the active tab ended up in overflow, swap it with the last visible tab
  if (hasOverflow && effectiveActiveTabId != null) {
    const activeInOverflow = overflowTabs.find((t) => t.id === effectiveActiveTabId);
    if (activeInOverflow != null) {
      const lastVisible = visibleTabs[visibleTabs.length - 1];
      visibleTabs = [...visibleTabs.slice(0, -1), activeInOverflow];
      overflowTabs = [lastVisible, ...overflowTabs.filter((t) => t.id !== effectiveActiveTabId)];
    }
  }

  const effectiveActiveTab = tabs.find((t) => t.id === effectiveActiveTabId);

  return (
    <div className={classes.container} {...getTestProps({component: 'TabbedViewerPane', element: 'root'})}>
      {/* Hidden bar for measuring actual tab widths */}
      <div ref={measureRef} className={classes.measureBar} aria-hidden={true}>
        {tabs.map((tab, index) => (
          <PillTab
            key={tab.id}
            tab={tab}
            index={index}
            isMeasurementOnly={true}
            isActive={false}
            onSelect={noop}
            onClose={noop}
          />
        ))}
        <div className={classes.overflowButtonSizer} />
      </div>

      <div className={classes.pillBar} role="tablist">
        {visibleTabs.map((tab, index) => (
          <PillTab
            key={tab.id}
            tab={tab}
            index={index}
            isActive={effectiveActiveTabId === tab.id}
            onSelect={() => {
              onTabChange(tab.id);
            }}
            onClose={() => {
              onTabClose(tab.id);
            }}
          />
        ))}
        {hasOverflow && (
          <Menu position="bottom-end" shadow="sm">
            <Menu.Target>
              <UnstyledButton
                {...getTestProps({component: 'TabbedViewerPane', element: 'overflowButton'})}
                className={classes.overflowButton}
                aria-label="More tabs"
              >
                <BenchlingIcon icon="overflow" />
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              {overflowTabs.map((tab, index) => (
                <Menu.Item
                  {...getTestProps({
                    component: 'TabbedViewerPane',
                    element: 'overflowItem',
                    index: String(index),
                  })}
                  key={tab.id}
                  onClick={() => {
                    onTabChange(tab.id);
                  }}
                >
                  {tab.label}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        )}
      </div>
      <div
        id={PANEL_ID}
        className={classes.content}
        role="tabpanel"
        aria-labelledby={effectiveActiveTabId != null ? `tab-${effectiveActiveTabId}` : undefined}
      >
        {effectiveActiveTab?.kind === 'artifact' && (
          <effectiveActiveTab.Component
            data={effectiveActiveTab.data}
            editable={effectiveActiveTab.editable}
          />
        )}
        {effectiveActiveTab?.kind === 'file' && (
          <FileViewerContent
            content={{
              chatSessionId: effectiveActiveTab.chatSessionId,
              externalFileId: effectiveActiveTab.externalFileId,
              filename: effectiveActiveTab.filename,
              mimeType: effectiveActiveTab.mimeType,
              label: effectiveActiveTab.label,
            }}
          />
        )}
      </div>
    </div>
  );
}

const OVERFLOW_BUTTON_SIZE = 30;

const useStyles = createStyles((theme) => {
  const tabBarBase = {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: theme.spacing.xs,
    overflow: 'hidden',
  } as const;

  return {
    container: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    measureBar: {
      ...tabBarBase,
      position: 'relative',
      padding: `0 ${theme.spacing.md}`,
      height: 0,
      visibility: 'hidden',
      pointerEvents: 'none',
    },
    pillBar: {
      ...tabBarBase,
      padding: `${theme.spacing.xs} ${theme.spacing.md}`,
      backgroundColor: theme.colors.gray[0],
      borderBottom: `1px solid ${theme.colors.gray[2]}`,
      flexShrink: 0,
    },
    pill: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      height: 26,
      padding: '0 4px 0 8px',
      fontSize: 12,
      fontWeight: 500,
      borderRadius: 999,
      border: `1px solid ${theme.colors.gray[2]}`,
      backgroundColor: theme.white,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      flex: 'none',
      color: theme.colors.gray[7],
      fontFamily: theme.fontFamily,
      transition: 'background 120ms ease, color 120ms ease, border-color 120ms ease',
      '&:hover': {
        backgroundColor: theme.colors.gray[1],
        color: theme.colors.dark[9],
        borderColor: theme.colors.gray[3],
      },
    },
    pillActive: {
      backgroundColor: theme.colors.blue[0],
      color: theme.colors.blue[8],
      borderColor: theme.colors.blue[2],
      '&:hover': {
        backgroundColor: theme.colors.blue[0],
        color: theme.colors.blue[8],
        borderColor: theme.colors.blue[3],
      },
    },
    pillDoc: {
      paddingRight: 10,
    },
    pillIcon: {
      fontSize: 11,
      color: theme.colors.gray[5],
      flexShrink: 0,
    },
    pillIconActive: {
      color: theme.colors.blue[8],
    },
    pillLabel: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      minWidth: 0,
    },
    closeButton: {
      flexShrink: 0,
      width: 16,
      height: 16,
      color: theme.colors.gray[5],
      backgroundColor: 'transparent',
      borderRadius: theme.radius.sm,
      '&:hover': {
        color: theme.colors.dark[9],
        backgroundColor: 'transparent',
      },
    },
    closeButtonActive: {
      color: theme.colors.blue[6],
      backgroundColor: 'transparent',
      '&:hover': {
        color: theme.colors.blue[8],
        backgroundColor: 'transparent',
      },
    },
    overflowButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: OVERFLOW_BUTTON_SIZE,
      height: OVERFLOW_BUTTON_SIZE,
      flexShrink: 0,
      borderRadius: theme.radius.md,
      border: 0,
      background: 'transparent',
      color: theme.colors.gray[6],
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.colors.gray[1],
        color: theme.colors.dark[9],
      },
    },
    overflowButtonSizer: {
      width: OVERFLOW_BUTTON_SIZE,
      height: OVERFLOW_BUTTON_SIZE,
      flexShrink: 0,
    },
    content: {
      flex: 1,
      overflow: 'auto',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      padding: `${theme.spacing.xs} 0 0 12px`,
    },
  };
});
